import React, { useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { FileText, Loader2, Scan, Globe } from 'lucide-react';

/**
 * Reusable "Screen Teaser" / "Screen CIM" / "Scrape Web" buttons for any section.
 *
 * Props:
 *   projectId    – project ID
 *   documents    – array of project documents [{id, filename, label, uploaded_at}]
 *   section      – "executive-summary" | "financials" | "phase1" | "phase2"
 *   screening    – boolean, whether this section is currently processing
 *   onStarted    – callback when screening kicks off
 *   label        – optional label override (e.g. "Phase 1 K.O.")
 *   companyWebsite – if set, show "Scrape Web" button
 */
export function ScreenDocButtons({ projectId, documents, section, screening, onStarted, label, companyWebsite }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerType, setPickerType] = useState(null);

  const teasers = (documents || []).filter(d => d.label === 'Teaser');
  const cims = (documents || []).filter(d => d.label === 'CIM');
  const hasTeasers = teasers.length > 0;
  const hasCIMs = cims.length > 0;
  const hasWebsite = !!(companyWebsite && companyWebsite.trim());

  const triggerScreen = async (docId) => {
    setPickerOpen(false);
    if (onStarted) onStarted();
    try {
      await api.screenSection(projectId, section, docId);
      toast.info(`Screening ${label || section} started...`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Screening failed');
    }
  };

  const triggerScrape = async () => {
    if (onStarted) onStarted();
    try {
      await api.scrapeSection(projectId, section);
      toast.info(`Scraping web for ${label || section}...`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scrape failed');
    }
  };

  const handleClick = (type) => {
    const docs = type === 'Teaser' ? teasers : cims;
    if (docs.length === 1) {
      triggerScreen(docs[0].id);
    } else {
      setPickerType(type);
      setPickerOpen(true);
    }
  };

  const pickerDocs = pickerType === 'Teaser' ? teasers : cims;
  const hasAnyAction = hasTeasers || hasCIMs;

  if (!hasAnyAction) return null;

  return (
    <>
      <div className="flex items-center gap-1.5" data-testid={`screen-buttons-${section}`}>
        {screening ? (
          <div className="flex items-center gap-1.5 text-blue-400 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Processing...</span>
          </div>
        ) : (
          <>
            {hasTeasers && (
              <Button
                data-testid={`screen-teaser-${section}`}
                onClick={() => handleClick('Teaser')}
                className="bg-blue-600/15 hover:bg-blue-600/25 text-blue-400 border border-blue-600/20 rounded-sm text-[10px] sm:text-[11px] h-6 sm:h-7 px-2 sm:px-2.5 font-medium"
              >
                <Scan className="w-3 h-3 mr-0.5 sm:mr-1" />
                <span className="hidden sm:inline">Screen </span>Teaser
              </Button>
            )}
            {hasCIMs && (
              <Button
                data-testid={`screen-cim-${section}`}
                onClick={() => handleClick('CIM')}
                className="bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-400 border border-emerald-600/20 rounded-sm text-[10px] sm:text-[11px] h-6 sm:h-7 px-2 sm:px-2.5 font-medium"
              >
                <Scan className="w-3 h-3 mr-0.5 sm:mr-1" />
                <span className="hidden sm:inline">Screen </span>CIM
              </Button>
            )}
          </>
        )}
      </div>

      {/* Document Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground">
              Select {pickerType} to Screen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <p className="text-xs text-muted-foreground">
              Multiple {pickerType}s found. Choose which one to use for {label || section}:
            </p>
            {pickerDocs.map(d => (
              <button
                key={d.id}
                onClick={() => triggerScreen(d.id)}
                className="w-full text-left p-3 rounded-sm border border-border hover:border-blue-600/30 hover:bg-blue-600/5 transition-colors"
                data-testid={`picker-doc-${d.id}`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{d.filename}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Uploaded {new Date(d.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
